import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, renderSlot as _renderSlot, resolveDirective as _resolveDirective, withDirectives as _withDirectives, unref as _unref, mergeProps as _mergeProps, withCtx as _withCtx, createBlock as _createBlock } from "vue"

import { QInfiniteScroll } from "quasar"
import { computed, PropType, ref, watch } from "vue"

//region Props and Emits

const ITEMS_TO_RENDER = 25
// Offset (pixels) to bottom of Infinite Scroll container from which the component should start loading more content in advance
const OFFSET_FROM_BOTTOM = 250


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxInfiniteScroll',
  props: {
  dataList: {
    type: Array as PropType<unknown[]>,
    required: true,
  },

  // In order for the list to correctly be updated on changes, we need to provide unique index.
  // We cannot simply use the v-for index, because array sort would then not correctly update the data.
  // You need to provide a function that has the infinite list entity and returns some unique ID.
  idSelectorFunction: {
    type: Function as PropType<(entity: unknown) => string | undefined>,
  },

  // If we have multiple sfxInfiniteScroll lists or are struggling to determine the correct container height/scroll,
  // then we can simply increase the offset by the height of all the other components below this current one.
  // Note that this is EXTRA, meaning that it will be appended to the already existing default offset.
  extraOffset: {
    type: Number,
    default: 0,
  },
},
  setup(__props, { expose: __expose }) {

const props = __props

// Expose the ability to reset so that we can do it from outside.
// The use case for that would be when we sort the data -> the data source is not really changes,
// just the elements inside. We cannot set the data source listener to "deep" since we cannot ensure that the internals
// of the elements won't change, and we won't know in which cases we would want to trigger a reset.
__expose({ resetState })

//endregion

//region Variables

// Number of data items to load per time
const offset = computed<number>(() => OFFSET_FROM_BOTTOM + props.extraOffset)

const infiniteScroll = ref<QInfiniteScroll>()
const listWithId = ref<Array<(typeof props.dataList)[0]>>([])

const onIntersection = {
  // If the element is not on the screen, then make it invisible, else make it visible
  handler(entry: IntersectionObserverEntry) {
    if (entry.isIntersecting) {
      entry.target.classList.remove("invisible")
    } else {
      entry.target.classList.add("invisible")
    }
  },
  cfg: { rootMargin: "500px 0px" }, // Make the intersection box +500px on Y axis.
}

//endregion

//region Functions

// Quasar infinite scroll will call this function once it reaches near the bottom

function onLoad(index: number, done: () => void) {
  // The q-infinite list has index based on the number of times this function is triggered.
  // So in order to continue where we left of, we simply multiply the index based on the amount of items rendered.
  // Also, the index starts from 1, so we manually subtract 1 from it to adjust.
  const startIndex = ITEMS_TO_RENDER * (index - 1)
  const listLength = props.dataList?.length

  for (let i = 0; i <= ITEMS_TO_RENDER; i++) {
    const currentIndex = startIndex + i

    // If the index is higher than total number of elements in the list, then we reached the end, abort.
    // Index starts from 0, so just subtract 1 from the total amount, so we don't get off 1 error.
    if (currentIndex > listLength - 1) break

    const item = props.dataList[currentIndex]
    listWithId.value.push(item)
  }

  // function to tell the scroll that we are done loading the data
  done()

  // There are no more items to render so stop detecting that the infinite list is past the offset value
  // and triggering this load function.
  if (listLength < startIndex) {
    infiniteScroll.value?.stop()
  }
}

function resetState() {
  // The data source has modified its data, so we have to reset the whole rendering list
  listWithId.value.length = 0
  // Also reset the infinite scroll, so that onLoad index would be set to 0
  infiniteScroll.value?.reset()
  // If the infinite list has stopped due to reaching the end, we need to attempt to resume it
  infiniteScroll.value?.resume()
  // Need to manually trigger the first onLoad function, just in case of DOM elements rendering out of sync
  infiniteScroll.value?.trigger()
}

//endregion

//region Setup Code

watch(
  () => props.dataList,
  () => {
    resetState()
  },
  { immediate: true }
)

//endregion

return (_ctx: any,_cache: any) => {
  const _directive_intersection = _resolveDirective("intersection")!

  return (_openBlock(), _createBlock(_unref(QInfiniteScroll), _mergeProps({
    offset: offset.value,
    onLoad: onLoad,
    ref_key: "infiniteScroll",
    ref: infiniteScroll
  }, _ctx.$attrs), {
    default: _withCtx(() => [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(listWithId.value, (entity, index) => {
        return _withDirectives((_openBlock(), _createElementBlock("div", {
          key: props.idSelectorFunction?.(entity) + index || index
        }, [
          _renderSlot(_ctx.$slots, "default", { data: entity })
        ])), [
          [_directive_intersection, onIntersection]
        ])
      }), 128))
    ]),
    _: 3
  }, 16, ["offset"]))
}
}

})